/* eslint-disable no-restricted-syntax */
export const IST_TIMEZONE = 'Asia/Kolkata';

/** IST offset from UTC in milliseconds (+5:30 = 19800000ms) */
const IST_OFFSET_MS = 5 * 60 * 60 * 1000 + 30 * 60 * 1000;

// Cache formatters at module level for performance
const _istFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: IST_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

// Helper: returns IST components from any Date
function _getISTparts(date: Date) {
  const parts = _istFormatter.formatToParts(date);
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  if (get('year') < 2000) {
    throw new Error(
      'Intl.DateTimeFormat.formatToParts returned invalid data — Hermes may lack full ICU. Add @formatjs/intl-datetimeformat polyfill.'
    );
  }
  return {
    year: get('year'),
    month: get('month') - 1, // 0-indexed like JS Date
    day: get('day'),
    hour: get('hour') === 24 ? 0 : get('hour'), // handle midnight
    minute: get('minute'),
    second: get('second'),
    dayOfWeek: new Date(get('year'), get('month') - 1, get('day')).getDay(), // 0=Sun
  };
}

/** Get IST hour (0-23) from any Date */
export function getISTHour(date: Date): number {
  return _getISTparts(date).hour;
}

/** Get IST day of week (0=Sun, 6=Sat) from any Date */
export function getISTDay(date: Date): number {
  return _getISTparts(date).dayOfWeek;
}

/** Get IST day of month (1-31) from any Date */
export function getISTDate(date: Date): number {
  return _getISTparts(date).day;
}

/** Get IST month (0-11) from any Date */
export function getISTMonth(date: Date): number {
  return _getISTparts(date).month;
}

/** Get IST full year from any Date */
export function getISTYear(date: Date): number {
  return _getISTparts(date).year;
}

/** Get IST minute (0-59) from any Date */
export function getISTMinute(date: Date): number {
  return _getISTparts(date).minute;
}

/** Get IST second (0-59) from any Date */
export function getISTSecond(date: Date): number {
  return _getISTparts(date).second;
}

/** Format any Date as YYYY-MM-DD in IST. Replaces toISOString().split('T')[0] */
export function toISTDateString(date: Date): string {
  const { year, month, day } = _getISTparts(date);
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Get today's date as YYYY-MM-DD in IST */
export function getISTToday(): string {
  return toISTDateString(new Date());
}

/** Format Date as HH:MM string in IST. Replaces getHours().padStart + ':' + getMinutes().padStart pattern */
export function formatISTHHMM(date: Date): string {
  const { hour, minute } = _getISTparts(date);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** IST-safe locale datetime string. Replaces .toLocaleString('en-IN', {...}) without timeZone */
export function formatISTDateTimeLocale(
  date: Date,
  options: Omit<Intl.DateTimeFormatOptions, 'timeZone'>
): string {
  return date.toLocaleString('en-IN', { ...options, timeZone: IST_TIMEZONE });
}

/**
 * Returns a UTC Date representing the start of the IST calendar day (00:00:00.000 IST).
 * Replaces startOfDay() from date-fns and .setHours(0,0,0,0) on raw Date objects.
 * Works in Node.js, Browser, and React Native (Intl-based, no date-fns-tz dependency).
 */
export function toISTStartOfDay(date: Date): Date {
  const { year, month, day } = _getISTparts(date);
  // IST 00:00:00 = UTC 00:00:00 minus 5h30m = Date.UTC(y,m,d,0,0,0) - IST_OFFSET_MS
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0) - IST_OFFSET_MS);
}

/**
 * Returns a UTC Date representing the end of the IST calendar day (23:59:59.999 IST).
 * Replaces endOfDay() from date-fns and .setHours(23,59,59,999) on raw Date objects.
 */
export function toISTEndOfDay(date: Date): Date {
  const { year, month, day } = _getISTparts(date);
  return new Date(Date.UTC(year, month, day, 23, 59, 59, 999) - IST_OFFSET_MS);
}

/**
 * Returns a UTC Date representing the start of the IST calendar month (1st day, 00:00:00.000 IST).
 * Replaces startOfMonth() from date-fns.
 */
export function toISTStartOfMonth(date: Date): Date {
  const { year, month } = _getISTparts(date);
  return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0) - IST_OFFSET_MS);
}

/**
 * Returns a UTC Date representing the end of the IST calendar month (last day, 23:59:59.999 IST).
 * Replaces endOfMonth() from date-fns.
 */
export function toISTEndOfMonth(date: Date): Date {
  const { year, month } = _getISTparts(date);
  // Last day of month: day 0 of next month = last day of current month
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(Date.UTC(year, month, lastDay, 23, 59, 59, 999) - IST_OFFSET_MS);
}

/** IST-safe locale date string. Always uses timeZone: 'Asia/Kolkata' */
export function formatISTLocale(
  date: Date,
  options: Omit<Intl.DateTimeFormatOptions, 'timeZone'>
): string {
  return date.toLocaleDateString('en-IN', { ...options, timeZone: IST_TIMEZONE });
}

/** IST-safe locale time string. Always uses timeZone: 'Asia/Kolkata' */
export function formatISTTimeLocale(
  date: Date,
  options: Omit<Intl.DateTimeFormatOptions, 'timeZone'>
): string {
  return date.toLocaleTimeString('en-IN', { ...options, timeZone: IST_TIMEZONE });
}

/**
 * Helper: resolve string | Date | null | undefined to a valid Date, or null.
 * Returns null for falsy, invalid, or unparseable inputs.
 */
function _resolveDate(date: string | Date | null | undefined): Date | null {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format date as "24 Jan 2026" (IST).
 * Returns '-' for null/undefined/invalid.
 *
 * @example
 * formatDate('2026-01-24T10:30:00Z')  // "24 Jan 2026"
 * formatDate(null)                     // "-"
 */
export function formatDate(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  return formatISTLocale(d, { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Format date as "24 January 2026" — long month name for invoices, refund emails.
 * Returns '-' for null/undefined/invalid.
 */
export function formatDateLong(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  return formatISTLocale(d, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Format date as "Monday, 24 January 2026" — full weekday + date for booking reminders.
 * Returns '-' for null/undefined/invalid.
 */
export function formatDateFull(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  return formatISTLocale(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Format date as "Mon, 24 Jan" — compact weekday for dashboards.
 * Returns '-' for null/undefined/invalid.
 */
export function formatDateWithWeekday(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  return formatISTLocale(d, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Format date compactly: "24 Jan" (current IST year) or "24 Jan 2025" (prior year).
 * Used on booking cards where year is omitted for recency.
 * Returns '-' for null/undefined/invalid.
 */
export function formatDateCompact(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  const { year: istYear } = _getISTparts(d);
  const currentYear = _getISTparts(new Date()).year;
  if (istYear === currentYear) {
    return formatISTLocale(d, { day: 'numeric', month: 'short' });
  }
  return formatISTLocale(d, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Format date as "24/01/2026" — DD/MM/YYYY for Excel exports and tables.
 * Returns '-' for null/undefined/invalid.
 */
export function formatDateNumeric(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  return formatISTLocale(d, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Format date and time as "24 Jan 2026, 4:00 pm" — for admin timestamps.
 * Returns '-' for null/undefined/invalid.
 */
export function formatDateTimeMedium(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: IST_TIMEZONE,
  });
}

/**
 * Format date as a relative time string: "Just now", "5 minutes ago", "2 hours ago",
 * "Yesterday", "3 days ago". Falls back to formatDate() for dates older than 7 days.
 * Returns '-' for null/undefined/invalid.
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  const d = _resolveDate(date);
  if (!d) return '-';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return formatDate(d); // future dates
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return formatDate(d);
}

/**
 * Format time only (no date) with null handling.
 * Addresses DATA-02: Time displays correctly, DATA-04: Null handling
 *
 * @param time - Date string, Date object, or null/undefined
 * @returns Formatted time string or '-' for null/undefined/invalid
 *
 * @example
 * formatTime('2026-01-24T10:30:00Z')  // "4:00 pm" (IST)
 * formatTime(null)                    // "-"
 */
export function formatTime(time: string | Date | null | undefined): string {
  if (!time) return '-';
  const d = typeof time === 'string' ? new Date(time) : time;
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: IST_TIMEZONE,
  });
}

/**
 * Format date and time with null handling and timezone awareness.
 * Addresses DATA-02: Date/time displays correctly, DATA-04: Null handling
 *
 * @param dateTime - Date string, Date object, or null/undefined
 * @returns Formatted datetime string or '-' for null/undefined/invalid
 *
 * @example
 * formatDateTime('2026-01-24T10:30:00Z')  // "24/1/2026, 4:00 pm" (IST)
 * formatDateTime(null)                    // "-"
 */
export function formatDateTime(dateTime: string | Date | null | undefined): string {
  if (!dateTime) return '-';
  const d = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { timeZone: IST_TIMEZONE });
}

/** IST-aware day of week (0=Sun, 6=Sat). Replaces naive .getDay() calls. */
export function getDayOfWeek(date: string | Date): number {
  return getISTDay(typeof date === 'string' ? new Date(date) : date);
}

export function isWeekday(date: string | Date): boolean {
  const day = getDayOfWeek(date);
  return day >= 1 && day <= 5;
}

export function isWeekend(date: string | Date): boolean {
  const day = getDayOfWeek(date);
  return day === 0 || day === 6;
}
